// components/MovieGrid.js
import React, { useEffect, useState, useRef } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

/**
 * Format IMDb rating for display. Handles both normal (8.8) and
 * scaled integer (88) values from DynamoDB Decimal deserialization.
 */
const formatRating = (rating) => {
    if (!rating || rating === 'N/A') return null;
    const num = typeof rating === 'string' ? parseFloat(rating) : rating;
    if (isNaN(num)) return null;
    // If rating > 10, it's likely stored as scaled integer (e.g., 88 = 8.8)
    const normalized = num > 10 ? num / 10 : num;
    return normalized.toFixed(1);
};

/**
 * Clean stringified Python list values from DynamoDB.
 * E.g., "['Drama', 'Action']" → "Drama, Action"
 */
const cleanFieldValue = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        // Remove brackets and quotes
        return trimmed.slice(1, -1).replace(/'/g, '').replace(/"/g, '').trim();
    }
    return value;
};

const handleMovieClick = (movie, onMovieClick) => {
  // If onMovieClick callback is provided (from parent), use it for similarity view
  if (onMovieClick) {
    onMovieClick(movie);
  } else if (movie.imdbID) {
    // Otherwise, open IMDb page as fallback
    window.open(`https://www.imdb.com/title/${movie.imdbID}/`, '_blank', 'noopener,noreferrer');
  }
};

// Define MovieThumbnail outside of the MovieGrid function
const MovieThumbnail = ({ movie, onMovieClick }) => {
    // console.debug('[MovieThumbnail] Rendering with movie:', movie);

    // Extract the year from title or date if available
    const getYearFromMovie = () => {
        if (movie.Year) return movie.Year;
        if (movie.Released && movie.Released.match(/\d{4}/)) {
            return movie.Released.match(/\d{4}/)[0];
        }
        if (movie.Title && movie.Title.match(/\(\d{4}\)/)) {
            return movie.Title.match(/\(\d{4}\)/)[0].replace(/[()]/g, '');
        }
        return '';
    };

    // Get a clean title without year
    const getCleanTitle = () => {
        if (!movie.Title) return 'Unknown Title';
        return movie.Title.replace(/\s*\(\d{4}\)\s*$/, '');
    };

    // Format runtime to be more readable
    const formatRuntime = (runtime) => {
        if (!runtime) return '';
        const match = runtime.match(/(\d+)/);
        if (match) {
            const minutes = parseInt(match[1]);
            if (minutes >= 60) {
                const hours = Math.floor(minutes / 60);
                const remainingMinutes = minutes % 60;
                return `${hours}h ${remainingMinutes}m`;
            }
            return `${minutes}m`;
        }
        return runtime;
    };

    const handleIMDbClick = (event) => {
        event.stopPropagation();
        if (movie.imdbID) {
            window.open(`https://www.imdb.com/title/${movie.imdbID}/`, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div
            className="movie-thumbnail"
            onClick={() => handleMovieClick(movie, onMovieClick)}
        >
            <div className="poster-container">
                {movie.Poster && movie.Poster !== 'N/A' ? (
                    <LazyLoadImage
                        src={movie.Poster}
                        alt={`${movie.Title} poster`}
                        effect="blur"
                        threshold={100}
                        placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                        onError={(e) => {
                            console.warn(`[MovieThumbnail] Image load error for ${movie.Title}:`, e);
                            e.target.onerror = null;
                            e.target.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
                        }}
                    />
                ) : (
                    <div className="no-poster">No Image Available</div>
                )}
                <div className="title-bar">{getCleanTitle()}</div>
            </div>

            <div className="movie-details">
                <h3>{getCleanTitle()}</h3>

                {/* Show most important details at the top */}
                {getYearFromMovie() && (
                    <div className="detail-row">
                        <span className="detail-label">Year:</span>
                        <span className="detail-value">{getYearFromMovie()}</span>
                    </div>
                )}

                {movie.Type && movie.Type !== 'N/A' && (
                    <div className="detail-row">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">{movie.Type.charAt(0).toUpperCase() + movie.Type.slice(1)}</span>
                    </div>
                )}

                {movie.Genre && movie.Genre !== 'N/A' && (
                    <div className="detail-row">
                        <span className="detail-label">Genre:</span>
                        <span className="detail-value">{movie.Genre}</span>
                    </div>
                )}

                {movie.Runtime && movie.Runtime !== 'N/A' && (
                    <div className="detail-row">
                        <span className="detail-label">Runtime:</span>
                        <span className="detail-value">{formatRuntime(movie.Runtime)}</span>
                    </div>
                )}

                {movie.imdbRating && movie.imdbRating !== 'N/A' && formatRating(movie.imdbRating) && (
                    <div className="detail-row">
                        <span className="detail-label">Rating:</span>
                        <span className="detail-value">{formatRating(movie.imdbRating)}/10</span>
                    </div>
                )}

                {/* Add IMDb button */}
                {movie.imdbID && movie.imdbID !== 'N/A' && (
                    <button
                        className="imdb-button"
                        onClick={handleIMDbClick}
                        title="View on IMDb"
                    >
                        View on IMDb
                    </button>
                )}

                {/* Show other details */}
                {Object.entries(movie).map(([key, value]) => {
                    // Skip keys we've already handled, internal fields, or empty values
                    const skipKeys = ['Title', 'Poster', 'Type', 'Genre', 'Runtime', 'imdbRating', 'Year', 'imdbID', 'Response',
                        'score', 'source', 'content', 'augmented_plot', 'Plot', 'imdbVotes', 'wikipedia_url',
                        'Metacritic_score', 'Rotten_Tomatoes_score', 'overallRating', 'Rating_Category',
                        'Runtime_Minutes', 'OscarWins', 'OtherWins', 'Nominations'];
                    if (!skipKeys.includes(key) && value && value !== 'N/A') {
                        const displayValue = cleanFieldValue(String(value));
                        // Format field name: camelCase/snake_case → readable
                        const displayKey = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
                        return (
                            <div key={key} className="detail-row">
                                <span className="detail-label">{displayKey}:</span>
                                <span className="detail-value">{displayValue}</span>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

const MovieGrid = ({ movies, isLoading, onMovieClick }) => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    // Add ref for the Grid component
    const gridRef = useRef(null);

    // Current row state to track pagination
    const [currentRow, setCurrentRow] = useState(0);

    // Add window resize listener
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // console.debug('[MovieGrid] Rendered with', movies.length, 'movies, isLoading:', isLoading);
        // if (movies.length > 0) {
        //     console.debug('[MovieGrid] First movie sample:', movies[0]);
        // }
    }, [movies, isLoading]);

    // Only show skeletons before the first chunk arrives. During streaming
    // search, keep lexical results visible while semantic enrichment runs.
    if (isLoading && movies.length === 0) {
        // console.debug('[MovieGrid] Showing loading skeletons');
        return (
            <div className="movie-grid">
                {Array.from({ length: 12 }).map((_, index) => (
                    <div key={index} className="movie-thumbnail-skeleton">
                        <div className="skeleton-image"></div>
                        <div className="skeleton-title"></div>
                    </div>
                ))}
            </div>
        );
    }

    // If no results, show a message
    if (!isLoading && movies.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#718096' }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto', display: 'block', marginBottom: '1rem' }}>
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
                <p style={{ fontSize: '1.25rem', fontWeight: '500' }}>No movies found</p>
                <p>Try a different search term or check your spelling</p>
            </div>
        );
    }

    // For smaller collections or mobile devices, use standard grid
    if (movies.length < 50 || windowSize.width < 768) {
        // console.debug('[MovieGrid] Rendering standard grid for', movies.length, 'movies');
        return (
            <div>
                <p className="total-results">Showing {movies.length} results</p>
                <div className="movie-grid">
                    {movies.map((movie, index) => {
                        const movieKey = movie.imdbID || `movie-${index}-${movie.Title?.replace(/\s+/g, '-')}-${movie.Year || ''}`;
                        return (
                            <MovieThumbnail
                                key={movieKey}
                                movie={movie}
                                onMovieClick={onMovieClick}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    // For larger collections, use improved virtualized grid
    // console.debug('[MovieGrid] Setting up virtualized grid for', movies.length, 'movies');

    // Calculate grid dimensions based on viewport
    const containerWidth = Math.min(windowSize.width - 40, 1200);
    const itemWidth = windowSize.width < 768 ? 150 : 180; // Smaller on mobile
    const itemHeight = windowSize.width < 768 ? 280 : 340; // Shorter on mobile

    // Calculate how many columns will fit
    const columnCount = Math.max(2, Math.floor(containerWidth / itemWidth));
    const columnWidth = containerWidth / columnCount;
    const rowCount = Math.ceil(movies.length / columnCount);

    // console.debug('[MovieGrid] Grid dimensions:', {
    //     containerWidth,
    //     columnCount,
    //     columnWidth,
    //     rowCount,
    //     itemHeight
    // });

    // Calculate grid height - show enough rows to fill most of the viewport
    const visibleRows = Math.min(Math.max(3, Math.floor((windowSize.height * 0.8) / itemHeight)), rowCount);
    const gridHeight = visibleRows * itemHeight;

    // Debug what's being rendered
    // console.debug(`[MovieGrid] Will render approximately ${visibleRows} rows x ${columnCount} columns = ${visibleRows * columnCount} items visible of ${movies.length} total`);

    // Function to handle the "Load More" button click
    const handleLoadMore = () => {
        // Calculate the next row to scroll to (5 rows down or to the end)
        const nextRow = Math.min(currentRow + 5, rowCount - visibleRows);

        // Only scroll if we're not already at the end
        if (nextRow > currentRow) {
            // Update the current row state
            setCurrentRow(nextRow);

            // Use the Grid's scrollToItem method to jump to the next set of results
            if (gridRef.current) {
                gridRef.current.scrollToItem({
                    align: 'start',
                    rowIndex: nextRow
                });

                // console.debug(`[MovieGrid] Scrolling to row ${nextRow}`);
            }
        }
    };

    return (
        <div className="virtualized-grid-container" style={{ height: 'auto', minHeight: gridHeight + 50 }}>
            <p className="total-results">Showing {movies.length} results</p>
            <InfiniteLoader
                isItemLoaded={index => index < movies.length}
                itemCount={movies.length}
                loadMoreItems={() => Promise.resolve()}
                threshold={10} // Increase threshold to load more items earlier
            >
                {({ onItemsRendered, ref }) => (
                    <Grid
                        ref={(grid) => {
                            // Store a reference to the grid
                            gridRef.current = grid;
                            // Pass the ref to InfiniteLoader
                            ref(grid);
                        }}
                        onItemsRendered={gridProps => {
                            const { visibleRowStartIndex, visibleRowStopIndex } = gridProps;
                            // console.debug('[MovieGrid] Grid rendering rows:',
                            //     visibleRowStartIndex, 'to', visibleRowStopIndex,
                            //     '(approx.', (visibleRowStopIndex - visibleRowStartIndex + 1) * columnCount, 'items)'
                            // );

                            onItemsRendered({
                                overscanStartIndex: visibleRowStartIndex * columnCount,
                                overscanStopIndex: (visibleRowStopIndex + 1) * columnCount - 1,
                                visibleStartIndex: visibleRowStartIndex * columnCount,
                                visibleStopIndex: (visibleRowStopIndex + 1) * columnCount - 1,
                            });

                            // Update current row based on visible rows
                            if (visibleRowStartIndex > currentRow) {
                                setCurrentRow(visibleRowStartIndex);
                            }
                        }}
                        columnCount={columnCount}
                        columnWidth={columnWidth}
                        height={gridHeight}
                        rowCount={rowCount}
                        rowHeight={itemHeight}
                        width={containerWidth}
                        overscanRowCount={5}
                        style={{ margin: '0 auto' }}
                        className="react-window-grid"
                    >
                        {({ columnIndex, rowIndex, style }) => {
                            const index = rowIndex * columnCount + columnIndex;
                            if (index >= movies.length) return null;

                            const movie = movies[index];

                            // Add padding inside each cell
                            const adjustedStyle = {
                                ...style,
                                padding: '10px',
                                boxSizing: 'border-box'
                            };

                            return (
                                <div style={adjustedStyle}>
                                    <MovieThumbnail
                                        movie={movie}
                                        onMovieClick={onMovieClick}
                                    />
                                </div>
                            );
                        }}
                    </Grid>
                )}
            </InfiniteLoader>

            {/* Add a "Load More" button at the bottom for easier navigation */}
            <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                <button
                    onClick={handleLoadMore}
                    style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        fontWeight: '500'
                    }}
                >
                    Load More Results
                </button>
            </div>
        </div>
    );
};

export default MovieGrid;
